import { useMemo } from 'react'

import { SUPPORTED_CHAINS } from '@/config/chain/constants'
import { useStore } from '@/domain/state'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { sandboxDialogConfig } from '@/features/dialogs/sandbox/SandboxDialog'
import { getTenderlyClient, TestnetClient } from '@/features/dialogs/sandbox/tenderly/TenderlyClient'

export interface OpenSandboxModalOptions {
  asDrawer?: boolean
}

export interface UseSandboxResult {
  isInSandbox: boolean
  sandboxChainId?: number
  originChainId?: number
  tenderlyClient: TestnetClient | undefined
  deleteSandbox: () => void
  enableSandbox: () => void
  disableSandbox: () => void
  openSandboxModal: (options?: { asDrawer?: boolean }) => void
}

export function useSandbox(): UseSandboxResult {
  const isSandboxFeatureEnabled = import.meta.env.VITE_FEATURE_SANDBOX === '1'
  const openDialog = useOpenDialog()

  if (!isSandboxFeatureEnabled) {
    return {
      isInSandbox: false,
      tenderlyClient: undefined,
      deleteSandbox: () => {},
      enableSandbox: () => {},
      disableSandbox: () => {},
      openSandboxModal: () => {},
    }
  }
  // biome-ignore lint/correctness/useHookAtTopLevel: <missing explanation>
  const { network, setNetwork } = useStore((state) => state.sandbox)

  // biome-ignore lint/correctness/useHookAtTopLevel: we need memo so that the client is not recreated on every render
  const tenderlyClient = useMemo(() => {
    if (!network) {
      return undefined
    }

    const originChain = SUPPORTED_CHAINS.find((chain) => chain.id === network.originChainId)
    if (!originChain) {
      return undefined
    }

    return getTenderlyClient({ rpcUrl: network.forkUrl, originChain, forkChainId: network.forkChainId })
  }, [network])

  return {
    isInSandbox: network?.sandboxEnabled === true,
    sandboxChainId: network?.forkChainId,
    originChainId: network?.originChainId,
    tenderlyClient,
    deleteSandbox: () => {
      setNetwork(undefined)
    },
    enableSandbox: () => {
      setNetwork((prev) => (prev ? { ...prev, sandboxEnabled: true } : prev))
    },
    disableSandbox: () => {
      setNetwork((prev) => (prev ? { ...prev, sandboxEnabled: false } : prev))
    },
    openSandboxModal: (options = {}) => {
      openDialog(sandboxDialogConfig, options)
    },
  }
}
